import React from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  label?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  xs: {
    root: 'w-[20px] h-[10px] rounded-full',
    thumb: 'w-[8px] h-[8px] translate-x-[2px]',
    checkedThumb: 'data-[state=checked]:translate-x-[11px]'
  },
  sm: {
    root: 'w-[32px] h-[18px] rounded-full',
    thumb: 'w-[15px] h-[15px] translate-x-[2px]',
    checkedThumb: 'data-[state=checked]:translate-x-[16px]'
  },
  md: {
    root: 'w-[42px] h-[22px] rounded-full',
    thumb: 'w-[15px] h-[15px] translate-x-[2px]',
    checkedThumb: 'data-[state=checked]:translate-x-[22px]'
  },
  lg: {
    root: 'w-[52px] h-[28px] rounded-full',
    thumb: 'w-[24px] h-[24px] translate-x-[2px]',
    checkedThumb: 'data-[state=checked]:translate-x-[26px]'
  }
};

export const CustomSwitch: React.FC<SwitchProps> = ({
  checked,
  onCheckedChange,
  disabled = false,
  id,
  className = '',
  label,
  size = 'md'
}) => {
  const { root, thumb, checkedThumb } = sizeStyles[size];

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className={`text-sm font-medium select-none ${disabled
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 cursor-pointer'
            }`}
        >
          {label}
        </label>
      )}
      <RadixSwitch.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={`
          ${root}
          bg-gray-200
          data-[state=checked]:bg-green-500
          hover:bg-gray-300
          data-[state=checked]:hover:bg-green-600
          transition-colors duration-200
          relative
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-500
        `}
      >
        <RadixSwitch.Thumb
          className={`
            block
            bg-white
            rounded-full
            shadow
            transition-transform duration-200
            transform ${thumb} ${checkedThumb}
            will-change-transform
          `}
        />
      </RadixSwitch.Root>
    </div>
  );
};

export default CustomSwitch;
